import {
  Button,
  Flex,
  IconButton,
  MarkdownEditor,
  Panel,
  PlusIcon,
  Text,
  TextField,
  XMarkIcon,
} from 'components'
import {
  AfterRegistrationConsent,
  RegistrationConsentItem,
  RegistrationConsentSubItem,
} from 'types'
import { useRegistrationConsent } from './RegistrationConsentProvider'

export default function AfterRegistrationConsentEditor() {
  const { consent, updateConsent } = useRegistrationConsent()

  function update(updates: Partial<AfterRegistrationConsent>) {
    updateConsent({ after: { ...consent?.after!, ...updates } })
  }

  function updateItems(items: RegistrationConsentItem[]) {
    update({ items })
  }

  const { text, items = [] } = consent?.after || {}

  return (
    <Flex direction="column" gap={4}>
      <MarkdownEditor
        value={text?.en || ''}
        onChange={en => update({ text: { en } })}
        placeholder="Description of the consent..."
      />

      <Flex direction="column" gap={3}>
        <Flex justify="space-between" align="center">
          <Text textStyle="bodyMedium" fontWeight="medium">
            Consent Items
          </Text>
          <Button
            leftIcon={<PlusIcon />}
            variant="ghost"
            size="sm"
            onClick={() => {
              const newItem: RegistrationConsentItem = { yesText: { en: '' } }
              updateItems([...items, newItem])
            }}
          >
            Add Item
          </Button>
        </Flex>

        {items.map((item, itemIndex) => (
          <ConsentItemEditor
            key={itemIndex}
            item={item}
            onUpdate={updatedItem => {
              const newItems = [...items]
              newItems[itemIndex] = updatedItem
              updateItems(newItems)
            }}
            onRemove={() => {
              updateItems(items.filter((_, index) => index !== itemIndex))
            }}
          />
        ))}
      </Flex>
    </Flex>
  )
}

interface ConsentItemEditorProps {
  item: RegistrationConsentItem
  onUpdate: (item: RegistrationConsentItem) => void
  onRemove: () => void
}

function ConsentItemEditor({ item, onUpdate, onRemove }: ConsentItemEditorProps) {
  function update(updates: Partial<RegistrationConsentItem>) {
    onUpdate({ ...item, ...updates })
  }

  const { subitems = [], yesText, noText, title } = item

  return (
    <Panel p={4} borderRadius="md" border="1px solid" borderColor="gray.200">
      <Flex direction="column" gap={3}>
        <Flex justify="space-between" align="flex-start" gap={2}>
          <Flex direction="column" flex={1} gap={2}>
            <TextField
              value={title?.en || ''}
              onChange={e => onUpdate({ ...item, title: { en: e.target.value } })}
              placeholder="Item title (optional)"
            />
            <MarkdownEditor
              value={yesText?.en || ''}
              onChange={en => update({ yesText: { en } })}
              placeholder="Text to display if yes..."
            />
            <MarkdownEditor
              value={noText?.en || ''}
              onChange={en => update({ noText: { en } })}
              placeholder="Text to display if no... Leave blank to bypass this item"
            />
          </Flex>
          <IconButton
            aria-label="Remove item"
            icon={<XMarkIcon />}
            variant="ghost"
            size="sm"
            onClick={onRemove}
          />
        </Flex>

        <Flex direction="column" gap={2}>
          <Flex justify="space-between" align="center">
            <Text textStyle="bodySmall" fontWeight="medium">
              Sub-items
            </Text>
            <Button
              leftIcon={<PlusIcon />}
              variant="ghost"
              size="xs"
              onClick={() => {
                const newSubitem: RegistrationConsentSubItem = {
                  title: { en: '' },
                  description: { en: '' },
                }
                update({ subitems: [...subitems, newSubitem] })
              }}
            >
              Add Sub-item
            </Button>
          </Flex>

          {subitems.map((subitem, subitemIndex) => (
            <SubItemEditor
              key={subitemIndex}
              subitem={subitem}
              onUpdate={updatedSubitem => {
                const newSubitems = [...subitems]
                newSubitems[subitemIndex] = updatedSubitem
                update({ subitems: newSubitems })
              }}
              onRemove={() => {
                update({ subitems: subitems.filter((_, index) => index !== subitemIndex) })
              }}
            />
          ))}
        </Flex>
      </Flex>
    </Panel>
  )
}

interface SubItemEditorProps {
  subitem: RegistrationConsentSubItem
  onUpdate: (subitem: RegistrationConsentSubItem) => void
  onRemove: () => void
}

function SubItemEditor({ subitem, onUpdate, onRemove }: SubItemEditorProps) {
  return (
    <Panel p={3} borderRadius="sm" bg="gray.50">
      <Flex direction="column" gap={2}>
        <Flex justify="space-between" align="center" gap={2}>
          <TextField
            value={subitem.title.en || ''}
            onChange={e => onUpdate({ ...subitem, title: { en: e.target.value } })}
            placeholder="Sub-item title"
            size="sm"
            flex={1}
          />
          <IconButton
            aria-label="Remove sub-item"
            icon={<XMarkIcon />}
            variant="ghost"
            size="xs"
            onClick={onRemove}
          />
        </Flex>
        <MarkdownEditor
          value={subitem.description?.en || ''}
          onChange={en => onUpdate({ ...subitem, description: { en } })}
          placeholder="Sub-item description..."
        />
      </Flex>
    </Panel>
  )
}
